import { formatter } from '../assets/js/field_config/formatter-gantt';

let sectorCache = null;

const getSectorFromCache = () => {
  if (!sectorCache) {
    const sectorData = sessionStorage.getItem('currentSector');
    sectorCache = sectorData ? JSON.parse(sectorData) : null;
  }
  return sectorCache;
};

const relationTypeMap = {
  fs: '0',
  ss: '1',
  ff: '2',
  sf: '3'
};

const fromCodeToNumber = (relationType) => relationTypeMap[relationType] || '0';

const getConstraintType = (constraint) => {
  const constraintMap = {
    'As Soon As Possible': 'asap',
    'As Late As Possible': 'alap',
    'Start No Later Than': 'snlt',
    'Start No Earlier Than': 'snet',
    'Must Start On': 'mso',
    'Must Finish On': 'mfo',
    'Finish No Later Than': 'fnlt',
    'Finish No Earlier Than': 'fnet'
  };

  return constraintMap[constraint] || 'asap';
};

const parseNewActivitiesArray = (activity) => {
  try {
    if (activity.newActivitiesArray) {
      if (typeof activity.newActivitiesArray === 'string') {
        return JSON.parse(activity.newActivitiesArray);
      }
      return activity.newActivitiesArray;
    }
    return [];
  } catch {
    return [];
  }
};

const transformDaysToHours = (days) => {
  const currentSector = getSectorFromCache();
  const activityBDDuration = days + 'days';
  const hoursDuration = formatter(currentSector).parse(activityBDDuration);
  return hoursDuration;
};

const calculateDurations = (activity) => {
  const hoursDuration = transformDaysToHours(activity.duration);
  const sumOfDurationRecursively = transformDaysToHours(
    activity.sumOfDurationRecursively
  );

  return {
    duration: hoursDuration,
    sumOfDurationRecursively
  };
};

const correctProjectParent = (activity, ganttTask) => {
  if (
    activity.correlative_id === 0 &&
    activity.type === 'project' &&
    ganttTask.parent !== 0 &&
    ganttTask.parent !== '0'
  ) {
    ganttTask.parent = '0';
  }
};

const createBaseGanttTask = (activity, cachedSector) => ({
  text: activity.name,
  id: parseInt(activity.unique_id),
  parent: activity.parent_id,
  description: activity.description || '',
  progress: activity.progress,
  constraint_type: getConstraintType(activity.constraint),
  constraint_date: activity.constraint_date
    ? new Date(activity.constraint_date)
    : null,
  start_date: new Date(activity.start_date),
  sectorId: activity.sectorId || cachedSector?.id,
  ganttId: activity.ganttId,
  proplannerId: activity.id,
  type: activity.type,
  non_parsed_original_start_date: activity.start_date,
  non_parsed_original_end_date: activity.end_date,
  non_parsed_original_constraint_date: activity.constraint_date,
  should_correct_start_date: true,
  correlative_id: activity.correlative_id,
  unique_correlative_id: activity.unique_correlative_id,
  is_lookahead: activity.isOnLookahead,
  should_be_showed: true,
  baseline_points: activity.baseline_points,
  hhWorkTime: activity.hhWorkTime,
  cost: activity.cost,
  used_cost: activity.used_cost || 0,
  real_cost: activity.real_cost || 0,
  real_work: activity.real_work || 0,
  activityModifications: activity.activitymodifications,
  calendar_id: activity.calendarId,
  ponderator: activity.ponderator,
  tasks: activity.tasks,
  responsables: activity.responsables,
  tags: activity.tags,
  subcontractId: activity.subcontractId,
  is_critical: activity.is_critical ? 'Si' : 'No',
  non_parsed_freeslack: activity.freeSlack,
  non_parsed_ponderator: activity.ponderator,
  non_parsed_is_critical: activity.is_critical,
  hasNewActivities: activity.hasNewActivities,
  isNewActivity: activity.isNewActivity
});

const applyGanttTaskModifications = (ganttTask, activity) => {
  if (activity.progress !== 0 && activity.type !== 'milestone') {
    ganttTask.auto_scheduling = false;
  }

  ganttTask.newActivitiesArray = parseNewActivitiesArray(activity);

  const durations = calculateDurations(activity);
  ganttTask.duration = durations.duration;
  ganttTask.sumOfDurationRecursively = durations.sumOfDurationRecursively;
  ganttTask.duration_milestone_bugged = durations.duration;
  ganttTask.for_disable_milestone_duration = durations.duration;
  ganttTask.non_parsed_original_duration = durations.duration;

  ganttTask.real_constraint_type = ganttTask.constraint_type;

  correctProjectParent(activity, ganttTask);

  if (ganttTask.type !== 'milestone') {
    ganttTask.color = '#00000000';
  }
};

export const convertActivityToGanttTask = (activity) => {
  const cachedSector = getSectorFromCache();
  const ganttTask = createBaseGanttTask(activity, cachedSector);

  applyGanttTaskModifications(ganttTask, activity);

  return ganttTask;
};

export const convertActivityRelationToLink = (activityRelation) => {
  const link = {
    id: parseInt(activityRelation.unique_id),
    source: activityRelation.source_id,
    target: activityRelation.target_id,
    type: fromCodeToNumber(activityRelation.type),
    ganttId: activityRelation.ganttId,
    sectorId: activityRelation.sectorId,
    proplannerId: activityRelation.id
  };

  const currentSector = getSectorFromCache();
  const activityBDDuration = (activityRelation.lag || 0) + 'days';
  link.lag = formatter(currentSector).parse(activityBDDuration);

  return link;
};

export const convertActivitiesBatch = (activities, batchSize = 1000) => {
  const results = [];

  for (let i = 0; i < activities.length; i += batchSize) {
    const batch = activities.slice(i, i + batchSize);

    const validBatch = batch.filter(Boolean);
    const batchResults = validBatch
      .map(convertActivityToGanttTask)
      .filter(Boolean);

    results.push(...batchResults);
  }

  return results;
};

export const resetSectorCache = () => {
  sectorCache = null;
};
